<template>
  <div class="select-drop-down-wrapper" ref="wrapperRef">
    <div 
      :id="componentId" 
      class="main-selector" 
      :class="{ disabled: isDisabled, open: isDropdownOpen }"
      @click="toggleDropdown"
      :style="customStyle"
    >
      <div v-if="displayValue" class="selected-value">
        {{ displayValue }}
      </div>
      <div v-else class="placeholder">
        {{ placeholder }}
      </div>
      <VIcon name="chevron-down-line" class="icon-inner" :class="{ rotated: isDropdownOpen }"></VIcon>
    </div>

    <!-- Custom dropdown (teleported to body so it escapes ancestor stacking
         contexts and is never covered by sibling rows [APICC-3457]) -->
    <Teleport to="body">
    <div v-if="isDropdownOpen" class="dropdown-backdrop" @click="closeDropdown"></div>
    <div v-if="isDropdownOpen" class="dropdown-panel dropdown-panel--fixed" :style="panelPositionStyle" ref="panelRef">
      <ul class="dropdown-menu">
        <li v-if="enableSearch" class="search-item">
          <input
            v-model="searchText"
            type="text"
            placeholder="Search"
            class="search-input"
            @input="handleSearch"
          />
        </li>

        <li 
          v-if="isMultiSelect && isSelectAllEnabled"
          class="option-item select-all-item"
          @click.stop="handleSelectAllClick"
        >
          <input type="checkbox" :checked="isAllSelected" class="option-checkbox" readonly />
          <span class="option-title select-all-title">Select All</span>
        </li>
        
        <li 
          v-for="option in filteredOptions" 
          :key="getOptionKey(option)"
          :class="{ 
            'option-item': true,
            'selected': isOptionSelected(option)
          }"
          @click.stop="handleOptionClick(option)"
        >
          <input 
            v-if="isMultiSelect"
            type="checkbox"
            :checked="isOptionSelected(option)"
            class="option-checkbox"
            readonly
          />
          <span class="option-title">{{ option.title }}</span>
        </li>

        <li v-if="filteredOptions.length === 0" class="option-item no-results">
          <span class="option-title">No results found</span>
        </li>
      </ul>
    </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';

// Props
const props = defineProps({
  options: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Select Value'
  },
  isDisabled: {
    type: Boolean,
    default: false
  },
  isMultiSelect: {
    type: Boolean,
    default: false
  },
  isSelectAllEnabled: {
    type: Boolean,
    default: false
  },
  popoverSize: {
    type: String,
    default: 'cover'
  },
  enableSearch: {
    type: Boolean,
    default: true
  },
  clientSideSearch: {
    type: Boolean,
    default: true
  },
  customDisplayKey: {
    type: String,
    default: null
  },
  multiSelectSeparator: {
    type: String,
    default: ', '
  },
  maxDisplayItems: {
    type: Number,
    default: 3
  },
  customStyle: {
    type: Object,
    default: () => ({})
  }
});

// Emits
const emit = defineEmits(['change', 'searchTextChanged', 'update:modelValue']);

// v-model support
const modelValue = defineModel({
  type: [String, Number, Object, Array],
  default: null
});

// Reactive state
const componentId = ref(`select-dropdown-${Math.floor(Math.random() * 10000000000)}`);
const isDropdownOpen = ref(false);
const searchText = ref('');
const wrapperRef = ref(null);
const panelRef = ref(null);
// Fixed-position style for the teleported panel. Anchored to the trigger via
// getBoundingClientRect so it escapes ancestor stacking contexts/overflow and
// flips above the trigger when there isn't enough room below [APICC-3442/3457].
const panelPositionStyle = ref({});

// Computed properties
const selectedOptions = computed(() => {
  if (!modelValue.value) return [];
  
  if (props.isMultiSelect) {
    const values = Array.isArray(modelValue.value) ? modelValue.value : [];
    return props.options.filter(option => 
      values.some(val => compareValues(val, option.value) || compareValues(val, option.id) || compareValues(val, option))
    );
  } else {
    const option = props.options.find(opt => 
      compareValues(modelValue.value, opt.value) || 
      compareValues(modelValue.value, opt.id) ||
      compareValues(modelValue.value, opt)
    );
    return option ? [option] : [];
  }
});

const displayValue = computed(() => {
  if (selectedOptions.value.length === 0) return '';
  
  if (props.isMultiSelect) {
    const titles = selectedOptions.value.map(opt => opt.title);
    if (titles.length > props.maxDisplayItems) {
      const shown = titles.slice(0, props.maxDisplayItems).join(props.multiSelectSeparator);
      return `${shown} +${titles.length - props.maxDisplayItems} more`;
    }
    return titles.join(props.multiSelectSeparator);
  } else {
    const option = selectedOptions.value[0];
    return props.customDisplayKey 
      ? option[props.customDisplayKey] 
      : option.selectBoxValueToShow || option.title || option.value;
  }
});

const filteredOptions = computed(() => {
  if (!props.enableSearch || !searchText.value.trim()) {
    return props.options;
  }
  
  if (!props.clientSideSearch) {
    return props.options;
  }
  
  const searchLower = searchText.value.trim().toLowerCase();
  return props.options.filter(option => {
    const title = option.title?.toString().toLowerCase() || '';
    const value = option.value?.toString().toLowerCase() || '';
    return title.includes(searchLower) || value.includes(searchLower);
  });
});

const isAllSelected = computed(() => {
  if (!props.isMultiSelect || filteredOptions.value.length === 0) return false;
  const currentValues = Array.isArray(modelValue.value) ? modelValue.value : [];
  if (currentValues.length === 0) return false;
  return filteredOptions.value.every(option => 
    currentValues.some(val => 
      compareValues(val, option.value) || compareValues(val, option.id) || compareValues(val, option)
    )
  );
});

// Methods
const compareValues = (val1, val2) => {
  if (val1 === val2) return true;
  if (val1 == val2) return true;
  if (val1?.id != null && val2?.id != null && val1.id === val2.id) return true;
  return false;
};

const isOptionSelected = (option) => {
  return selectedOptions.value.some(selected => {
    if (compareValues(selected.value, option.value)) return true;
    if (selected.id != null && option.id != null && compareValues(selected.id, option.id)) return true;
    return false;
  });
};

const getOptionKey = (option) => {
  return option.id ?? option.value ?? option.title;
};

const toggleDropdown = () => {
  if (props.isDisabled) return;
  if (isDropdownOpen.value) {
    closeDropdown();
  } else {
    openDropdown();
  }
};

const openDropdown = () => {
  isDropdownOpen.value = true;
  // Decide direction once the panel is in the DOM so we can measure its height.
  nextTick(updatePlacement);
  window.addEventListener('resize', updatePlacement);
  window.addEventListener('scroll', updatePlacement, true);
};

const closeDropdown = () => {
  isDropdownOpen.value = false;
  searchText.value = '';
  panelPositionStyle.value = {};
  window.removeEventListener('resize', updatePlacement);
  window.removeEventListener('scroll', updatePlacement, true);
};

/**
 * Open the panel upward when there isn't enough room below the trigger but there
 * is more room above it, keeping the options inside the viewport.
 */
const updatePlacement = () => {
  if (!isDropdownOpen.value) return;
  const trigger = wrapperRef.value;
  if (!trigger) return;

  const rect = trigger.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  // Prefer the measured panel height; fall back to the CSS max-height (280px).
  const panelHeight = panelRef.value?.offsetHeight || 280;
  const gap = 4;

  // Flip above the trigger when there isn't enough room below but there is above.
  const dropUp = spaceBelow < panelHeight + gap && spaceAbove > spaceBelow;

  const style = {
    position: 'fixed',
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    zIndex: 100000,
  };
  if (dropUp) {
    // Anchor the panel's bottom to just above the trigger.
    style.bottom = `${window.innerHeight - rect.top + gap}px`;
  } else {
    style.top = `${rect.bottom + gap}px`;
  }
  panelPositionStyle.value = style;
};

onBeforeUnmount(() => {
  window.removeEventListener('resize', updatePlacement);
  window.removeEventListener('scroll', updatePlacement, true);
});

const handleOptionClick = (option) => {
  if (props.isMultiSelect) {
    handleMultiSelectClick(option);
  } else {
    handleSingleSelectClick(option);
  }
};

const handleSingleSelectClick = (option) => {
  modelValue.value = option.value;
  emit('change', {
    selectedValue: option.value,
    selectedOption: option
  });
  closeDropdown();
};

const handleMultiSelectClick = (option) => {
  const currentValues = Array.isArray(modelValue.value) ? [...modelValue.value] : [];
  const isSelected = currentValues.some(val => 
    compareValues(val, option.value) || compareValues(val, option.id) || compareValues(val, option)
  );
  
  let newValues;
  let newOptions;
  
  if (isSelected) {
    newValues = currentValues.filter(val => 
      !compareValues(val, option.value) && !compareValues(val, option.id) && !compareValues(val, option)
    );
    newOptions = selectedOptions.value.filter(opt => 
      !compareValues(opt.value, option.value) && !compareValues(opt.id, option.id)
    );
  } else {
    newValues = [...currentValues, option.value];
    newOptions = [...selectedOptions.value, option];
  }
  
  modelValue.value = newValues;
  emit('change', {
    selectedValues: newValues,
    selectedOptions: newOptions
  });
};

const handleSearch = () => {
  emit('searchTextChanged', searchText.value);
};

const handleSelectAllClick = () => {
  if (!props.isMultiSelect) return;
  
  const currentValues = Array.isArray(modelValue.value) ? [...modelValue.value] : [];
  
  if (isAllSelected.value) {
    const newValues = currentValues.filter(val => 
      !filteredOptions.value.some(opt => 
        compareValues(val, opt.value) || compareValues(val, opt.id) || compareValues(val, opt)
      )
    );
    modelValue.value = newValues;
    const newOptions = props.options.filter(opt => 
      newValues.some(val => compareValues(val, opt.value) || compareValues(val, opt.id) || compareValues(val, opt))
    );
    emit('change', { selectedValues: newValues, selectedOptions: newOptions });
  } else {
    const newValuesSet = new Set(currentValues);
    filteredOptions.value.forEach(opt => {
      if (!currentValues.some(val => compareValues(val, opt.value) || compareValues(val, opt.id) || compareValues(val, opt))) {
        newValuesSet.add(opt.value);
      }
    });
    const newValues = Array.from(newValuesSet);
    modelValue.value = newValues;
    const newOptions = props.options.filter(opt => 
      newValues.some(val => compareValues(val, opt.value) || compareValues(val, opt.id) || compareValues(val, opt))
    );
    emit('change', { selectedValues: newValues, selectedOptions: newOptions });
  }
};

// Watch for external option changes
watch(() => props.options, () => {
  // Options changed, potentially from server-side search
}, { deep: true });
</script>

<style scoped>
.select-drop-down-wrapper {
  width: 100%;
  position: relative;
}

.main-selector {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 10px 16px;
  overflow: hidden;
  user-select: none;
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
  border: 1px solid transparent;
  transition: border-color 0.2s;
}

.main-selector.open {
  border-color: var(--purple);
}

.main-selector.disabled {
  pointer-events: none;
  opacity: 0.5;
  cursor: not-allowed;
}

.main-selector .icon-inner {
  pointer-events: none;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: var(--gray-04);
  transition: transform 0.2s;
}

.main-selector .icon-inner.rotated {
  transform: rotate(180deg);
}

.selected-value,
.placeholder {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  font-size: 14px;
  color: var(--black);
}

.placeholder {
  color: var(--gray-04);
}

/* Dropdown */
.dropdown-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99999;
}

.dropdown-panel {
  background: var(--white);
  border-radius: 5px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  max-height: 280px;
  overflow-y: auto;
}

/* Teleported to body: positioned via inline fixed coords from the trigger. */
.dropdown-panel--fixed {
  position: fixed;
}

.dropdown-menu {
  list-style: none;
  margin: 0;
  padding: 0;
}

.search-item {
  padding: 10px;
  border-bottom: 1px solid var(--background-primary);
}

.search-input {
  width: 100%;
  border: none;
  background: var(--background-primary);
  border-radius: 5px;
  padding: 8px 12px;
  font-size: 14px;
  color: var(--black);
  outline: none;
}

.search-input::placeholder {
  color: var(--gray-04);
}

.search-input:focus {
  outline: 1px solid var(--purple);
}

.option-item {
  padding: 10px 14px;
  border-bottom: 1px solid var(--background-primary);
  color: var(--black);
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: background-color 0.15s;
}

.option-item.select-all-item {
  border-bottom: 2px solid var(--background-primary);
  font-weight: 500;
}

.option-item.select-all-item .select-all-title {
  color: var(--purple);
}

.option-item:last-child {
  border-bottom: none;
}

.option-item:hover {
  background-color: var(--purple-opac);
  color: var(--purple);
}

.option-item.selected {
  color: var(--purple);
  font-weight: 500;
}

.option-item.no-results {
  color: var(--gray-04);
  cursor: default;
}

.option-item.no-results:hover {
  background: none;
  color: var(--gray-04);
}

.option-checkbox {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  margin: 0;
  cursor: pointer;
  accent-color: var(--purple);
}

.option-title {
  flex: 1;
}
</style>
